import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import { type DocumentShare } from "~/api/types";
import { useDocumentShares } from "~/api/v2/useDocumentShares";
import { formatDate } from "~/helpers/date";
import ErrorState from "~/molecules/ErrorState";
import { ShareDocumentDialog } from "~/molecules/ShareDocumentDialog";

interface Props {
  documentId: number;
}

export const DocumentSharesTab = ({ documentId }: Props) => {
  const { t } = useTranslation();
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const {
    data: shares,
    isLoading,
    error,
    mutate,
  } = useDocumentShares(documentId);

  return (
    <div className="flex flex-col gap-4">
      <Button variant="outline" onClick={() => setIsShareDialogOpen(true)}>
        {t(($) => $.documentSharing.share)}
      </Button>
      <ShareDocumentDialog
        documentId={documentId}
        open={isShareDialogOpen}
        onOpenChange={setIsShareDialogOpen}
        onShared={() => void mutate()}
      />
      <SharesList shares={shares} isLoading={isLoading} error={error} />
    </div>
  );
};

const SharesList = ({
  shares,
  isLoading,
  error,
}: {
  shares: DocumentShare[] | undefined;
  isLoading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslation();
  if (isLoading) return <CenterSpinner />;
  if (error) return <ErrorState />;

  if (!shares || shares.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t(($) => $.documentSharing.sharesEmpty)}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-4">
      {shares.map((share) => (
        <li key={share.id} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {share.targetGovernanceUnit.name}
            </span>
            <Badge variant="outline">
              {t(($) => $.documentSharing.status[share.status])}
            </Badge>
          </div>
          <span className="text-muted-foreground text-sm">
            {t(($) => $.documentSharing.sharedOn, {
              date: formatDate(share.createdAt),
              version: `${share.sourceVersion.majorVersion}.${share.sourceVersion.minorVersion}`,
            })}
          </span>
          {share.targetArchivedAt && (
            <span className="text-muted-foreground text-sm">
              {t(($) => $.documentSharing.targetArchived)}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
};
